// packages/core/src/learning/core-modules/compounding-first-investing.ts
// ─────────────────────────────────────────────────────────────────────────
// NISK CORE PATH — Module 06: "Compounding & First Investing" (tier: growth)
// 5 lessons + checkpoint quiz + final test. Assembled to the real
// Lesson / ModuleAssessment / LearningModule types.
//
// Impact Lab links (verified present in ImpactLab_impactScenarios.ts):
//   'hundred-first-job', 'start-at-25-vs-35', 'start-at-18-vs-28'
// ─────────────────────────────────────────────────────────────────────────

import type {
	Lesson,
	LearningModule,
	ModuleAssessment,
} from '../LearningTypes';

const LESSON_1: Lesson = {
	id: 'compounding-first-investing--money-can-start-earning-too',
	title: 'Money can start earning too',
	description:
		'You learn that investing is not magic; it is giving your money a chance to work while you keep living your life.',
	pillar: 'compounding-investing',
	schoolsOfThought: ['personal-finance-basics', 'fi-math', 'first-100k'],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['compounding', 'first-investing', 'money-growth'],
	prerequisites: [],
	relatedLessons: [
		'compounding-first-investing--small-money-is-not-fake-money',
	],
	concepts: ['compounding', 'money-working', 'growth-habit'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Most people think money only grows when they earn more. That is only half the story. The other half is learning how money can start doing part of the work after you earn it.',
		},
		{
			type: 'paragraph',
			text: 'Compounding is what happens when growth starts building on top of earlier growth. You save or invest a small amount, it has a chance to grow, then future growth may happen on a larger base. The point is not that every year will be smooth. The point is that money left alone has a different job than money spent instantly.',
		},
		{
			type: 'bullet_list',
			items: [
				'Earned money comes from your labor',
				'Saved money gives you control',
				'Invested money gets exposure to growth',
				'Compounded money gives time something to work with',
			],
		},
		{
			type: 'callout',
			icon: '🧠',
			text: 'The belief shift is simple: money is not only something you use. It can become something that works beside you.',
		},
		{
			type: 'example',
			label: 'Small start',
			text: 'Imagine someone puts away $100 a month. That may not feel powerful at first. But the lesson is not “$100 makes you rich.” The lesson is that $100 repeated builds a habit, a base, and a future option. If it is invested and markets cooperate over time, it can become much more than the original dollars. If markets do not cooperate for a while, the habit still builds discipline and ownership.',
		},
		{
			type: 'paragraph',
			text: 'This matters because most people wait for a perfect income before they start. They think, “I will invest when I make real money.” But the early reps matter. You are not just building an account balance. You are building the identity of someone who keeps part of their money instead of letting every dollar disappear.',
		},
		{
			type: 'paragraph',
			text: 'Compounding also changes how you see small choices. A spare $50 is not just $50. It can be dinner, a subscription pile, extra runway, or a tiny worker for your future self. Nisk is not here to tell you which one to pick. It is here to make the tradeoff visible.',
		},
		{
			type: 'key_takeaway',
			text: 'Compounding starts when your money gets a job beyond being spent.',
		},
	],
	audioScript:
		'You do not need to understand Wall Street to understand the first big investing idea. Money can either leave your life forever, sit still for safety, or get put somewhere it has a chance to grow. That growth is not guaranteed. It will not move in a straight line. But the idea matters because it changes your relationship with money. You stop seeing every dollar as something that only matters today. You start seeing some dollars as future workers. Think about $100 a month. On its own, it sounds small. It may not change your life next week. But repeated for years, it becomes a habit. If invested through a reasonable long-term approach, it may also become a base that future growth builds on. That is compounding: growth building on earlier growth. The big mistake is waiting until you feel rich enough to begin learning. You do not need a huge amount to understand the behavior. You need a repeatable amount, a long view, and enough humility to know markets will move around. This lesson is not about telling you what to buy. It is about seeing the shift: earned money depends on you showing up. Compounded money gives time something to work with. That is why investing is not just a finance topic. It is an options topic. The more dollars you keep and put to work carefully, the more future choices you may create.',
	flashcards: [
		{
			id: 'compounding-first-investing--money-can-start-earning-too--fc-main',
			role: 'main_idea',
			front: 'What is compounding?',
			back: 'Growth building on earlier growth, so money has a chance to gain speed over time.',
		},
		{
			id: 'compounding-first-investing--money-can-start-earning-too--fc-watch',
			role: 'watch_out',
			front: 'What is the waiting trap?',
			back: 'Waiting for a perfect income before building the habit of keeping and growing money.',
		},
		{
			id: 'compounding-first-investing--money-can-start-earning-too--fc-use',
			role: 'use_it',
			front: 'How do you use this?',
			back: 'Start thinking of some dollars as future workers, not just today’s spending power.',
		},
	],
	applyAction: {
		type: 'openImpactScenario',
		label: 'See what $100/month can become',
		scenarioId: 'hundred-first-job',
	},
	revision: 1,
};

const LESSON_2: Lesson = {
	id: 'compounding-first-investing--small-money-is-not-fake-money',
	title: 'Small money is not fake money',
	description:
		'You learn why modest amounts can still matter when they are repeated and protected from constant interruption.',
	pillar: 'compounding-investing',
	schoolsOfThought: ['first-100k', 'frugality', 'fi-math'],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['small-investing', 'first-100k', 'investing-habit'],
	prerequisites: ['compounding-first-investing--money-can-start-earning-too'],
	relatedLessons: ['compounding-first-investing--time-is-the-multiplier'],
	concepts: ['small-amounts', 'repetition', 'first-capital'],
	blocks: [
		{
			type: 'paragraph',
			text: 'A lot of people quit before they start because the first amount looks too small. They see $25, $50, or $100 and think, “What is the point?” That thought is expensive because it kills the habit before math gets a chance to help.',
		},
		{
			type: 'paragraph',
			text: 'Small money matters because it teaches repetition. Repetition is the bridge between intention and a real financial life. You do not become stable because one month went perfectly. You become more stable because your system keeps showing up even when the month is normal, boring, or annoying.',
		},
		{
			type: 'bullet_list',
			items: [
				'$25 proves you can start',
				'$50 proves you can repeat',
				'$100 proves you can build',
				'$500 proves your system is getting stronger',
			],
		},
		{
			type: 'callout',
			icon: '🔁',
			text: 'The first dollars are not about looking impressive. They are about training your money to stop leaking out by default.',
		},
		{
			type: 'example',
			label: 'Normal-income example',
			text: 'Someone earning $20 an hour may not feel like an investor. But if they protect $100 a month, that is $1,200 a year. Over five years, that is $6,000 before any growth. Add possible long-term growth, and the habit becomes more powerful. More importantly, that person has proven they can keep money moving toward the future.',
		},
		{
			type: 'paragraph',
			text: 'The danger is treating investing like an all-or-nothing identity. People imagine investors as people with big salaries, fancy accounts, or perfect knowledge. But the first stage is much simpler: keep a piece, place it thoughtfully, understand the risk, and do not panic every time the number moves.',
		},
		{
			type: 'paragraph',
			text: 'Small amounts also give you emotional training. When the market goes down, a beginner with a modest amount can learn how volatility feels without their whole life depending on it. That experience matters. You are learning the behavior before the stakes get bigger.',
		},
		{
			type: 'key_takeaway',
			text: 'Small money becomes real money when repetition protects it.',
		},
	],
	audioScript:
		'The biggest lie beginners tell themselves is, “This amount is too small to matter.” That sounds humble, but it is usually just fear in disguise. Small money matters because it builds the system. A $50 habit does not need to impress anyone. It needs to prove that you can keep a promise to your future self. Think of it like the gym. The first workout does not change your body. But it changes the direction. Money works the same way. A small repeated amount starts creating a track for your life to run on. You earn, you keep some, you put it somewhere intentional, you learn, you repeat. That is not flashy. That is the point. Flashy money usually wants attention. Useful money buys options. There is also a second benefit: small amounts help you learn risk without being reckless. You can see balances move. You can feel the temptation to check too often. You can learn that growth is not smooth. That lesson is better learned early and modestly than later with money you cannot emotionally handle. So no, $25 or $100 is not fake money. It is the first brick. The mistake is disrespecting the brick because it is not yet a building.',
	flashcards: [
		{
			id: 'compounding-first-investing--small-money-is-not-fake-money--fc-main',
			role: 'main_idea',
			front: 'Why does small money matter?',
			back: 'Repeated small amounts build the habit, base, and discipline that larger money later depends on.',
		},
		{
			id: 'compounding-first-investing--small-money-is-not-fake-money--fc-watch',
			role: 'watch_out',
			front: 'What kills beginners early?',
			back: 'Believing the first amount is too small to matter.',
		},
		{
			id: 'compounding-first-investing--small-money-is-not-fake-money--fc-use',
			role: 'use_it',
			front: 'What should you protect?',
			back: 'A repeatable amount that trains your money to move toward future options.',
		},
	],
	applyAction: {
		type: 'runFreedomCost',
		label: 'Test a small monthly amount',
		amount: 100,
	},
	revision: 1,
};

const LESSON_3: Lesson = {
	id: 'compounding-first-investing--time-is-the-multiplier',
	title: 'Time is the multiplier',
	description:
		'You learn why starting earlier helps, why starting later still counts, and why time matters more than looking clever.',
	pillar: 'compounding-investing',
	schoolsOfThought: ['fi-math', 'fire-movement', 'boglehead-indexing'],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 2,
	tags: ['time-in-market', 'starting-early', 'late-start-investing'],
	prerequisites: [
		'compounding-first-investing--small-money-is-not-fake-money',
	],
	relatedLessons: [
		'compounding-first-investing--investing-is-paid-uncertainty',
	],
	concepts: ['time-horizon', 'early-start', 'late-starters'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Time is one of the few advantages normal people can actually use. You may not have a huge income yet. You may not know advanced investing language. But if you give money enough time, the math has more room to work.',
		},
		{
			type: 'paragraph',
			text: 'This is why starting early is powerful. Not because young people are smarter. Not because early investors never make mistakes. Starting early gives each dollar more years to potentially earn, recover, and compound. It also gives you more years to practice calm behavior.',
		},
		{
			type: 'callout',
			icon: '⏳',
			text: 'Time does not remove risk. It gives good habits more room to matter.',
		},
		{
			type: 'example',
			label: 'Early vs later',
			text: 'Imagine two people both invest small monthly amounts. One starts at 25. One starts at 35. The person starting at 25 gets ten extra years of contributions and ten extra years of possible growth. That gap can become large, even if the monthly amount is modest. The lesson is not shame. The lesson is that waiting has a cost.',
		},
		{
			type: 'paragraph',
			text: 'But here is the part people need to hear clearly: starting later is not failure. A 35-year-old, 40-year-old, or 45-year-old can still improve their options. The strategy may need different expectations, stronger savings, more income focus, or a longer runway, but the door is not closed.',
		},
		{
			type: 'bullet_list',
			items: [
				'Early gives more time',
				'Later still gives direction',
				'Consistency beats panic',
				'Realistic expectations beat fantasy',
			],
		},
		{
			type: 'paragraph',
			text: 'The common mistake is turning time into regret. Regret says, “I should have started earlier, so why bother?” Judgment says, “Starting earlier would have helped, so starting now matters.” That is the Nisk frame. You are not here to punish your past self. You are here to give your future self more options.',
		},
		{
			type: 'key_takeaway',
			text: 'The best time advantage is earlier, but the second advantage is refusing to wait longer.',
		},
	],
	audioScript:
		'Time is the multiplier. That does not mean time magically fixes everything. It means money habits have more room to work when you start sooner and keep going longer. If someone starts at 25 instead of 35, they are not just ten years ahead in deposits. They may also be ten years ahead in growth, learning, and emotional reps. That matters. But this lesson has to be honest without being cruel. Starting later is not a death sentence. It just means the plan may need to be more intentional. Maybe the savings rate matters more. Maybe income growth matters more. Maybe the timeline is different. But the habit still counts. The biggest trap is regret. Regret takes useful information and turns it into paralysis. It says, “I missed the best time, so why start?” That is backwards. If waiting had a cost, more waiting has a cost too. The lesson is not to obsess over what you should have done at 18, 25, or 30. The lesson is to understand that time is valuable and use the time in front of you with more respect. You do not need to be perfect. You need to stop donating years to hesitation.',
	flashcards: [
		{
			id: 'compounding-first-investing--time-is-the-multiplier--fc-main',
			role: 'main_idea',
			front: 'Why does time matter?',
			back: 'Time gives contributions and possible growth more years to build on each other.',
		},
		{
			id: 'compounding-first-investing--time-is-the-multiplier--fc-watch',
			role: 'watch_out',
			front: 'What does regret do?',
			back: 'It turns a useful lesson about starting earlier into paralysis.',
		},
		{
			id: 'compounding-first-investing--time-is-the-multiplier--fc-use',
			role: 'use_it',
			front: 'How should late starters think?',
			back: 'Starting later still counts; the plan may just need stronger habits and realistic expectations.',
		},
	],
	applyAction: {
		type: 'openImpactScenario',
		label: 'Compare starting 25 vs 35',
		scenarioId: 'start-at-25-vs-35',
	},
	revision: 1,
};

const LESSON_4: Lesson = {
	id: 'compounding-first-investing--investing-is-paid-uncertainty',
	title: 'Investing is paid uncertainty',
	description:
		'You learn that investing is not a smooth reward machine; it is accepting uncertainty in exchange for possible long-term growth.',
	pillar: 'compounding-investing',
	schoolsOfThought: [
		'boglehead-indexing',
		'efficient-market',
		'behavioral-finance',
	],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['investment-risk', 'volatility', 'beginner-investing'],
	prerequisites: ['compounding-first-investing--time-is-the-multiplier'],
	relatedLessons: [
		'compounding-first-investing--first-system-beats-perfect-timing',
	],
	concepts: ['risk-reward', 'volatility', 'market-behavior'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Investing sounds exciting until your balance drops. Then it stops feeling like a math lesson and starts feeling personal. That emotional punch is why beginners need the truth early: investing is paid uncertainty.',
		},
		{
			type: 'paragraph',
			text: 'The reason investments may grow more than cash over long periods is because they come with risk. Prices move. Businesses change. Markets overreact. Bad years happen. Sometimes the number goes down even when you did nothing wrong.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'If an investment can grow, it can also move against you. The risk is not a glitch. It is part of the deal.',
		},
		{
			type: 'example',
			label: 'First drop',
			text: 'Imagine you invest $500 and later see $450. A beginner may think, “I failed.” But a drop does not automatically mean failure. It may simply mean the market moved. The real question is whether you understood the risk before entering, whether the money had enough time, and whether the choice fit the purpose of that money.',
		},
		{
			type: 'paragraph',
			text: 'This is why investing money and emergency money should not be mentally treated the same. Money you may need soon needs stability. Money you are trying to grow over many years can usually tolerate more movement. Confusing those two jobs creates panic.',
		},
		{
			type: 'bullet_list',
			items: [
				'Cash protects short-term needs',
				'Investing accepts movement',
				'Long timelines help absorb volatility',
				'Panic turns temporary drops into permanent decisions',
			],
		},
		{
			type: 'paragraph',
			text: 'The common trap is chasing upside while pretending downside will not hurt. People want growth without discomfort. But discomfort is part of why potential growth exists. Nisk should teach users to respect that tradeoff, not fantasize around it.',
		},
		{
			type: 'key_takeaway',
			text: 'Investing is not free money; it is accepting uncertainty for a chance at long-term growth.',
		},
	],
	audioScript:
		'Investing is paid uncertainty. That is the cleanest way to say it. You are not being handed smooth growth as a reward for opening an account. You are accepting movement, risk, and emotional pressure because over long periods, ownership can potentially grow more than money sitting still. But the price is uncertainty. Your balance can drop. Sometimes it will drop when you did nothing wrong. That is where beginners get tested. They think a lower number means they made a mistake. Sometimes it does. But sometimes it just means markets move. This is why you need to understand the job of each dollar. Emergency money has a short-term job: be there. Investing money has a long-term job: accept movement for possible growth. Mixing those jobs creates stress. If rent money is invested and the market drops, you are not learning investing. You are creating a crisis. But if long-term money moves around, that movement may be part of the deal. The goal is not to make you fearless. Fearless people do dumb things. The goal is to make you honest. Know what risk is. Know what the money is for. Know that no one can promise smooth returns. Then your first investing habit has a chance to be calm instead of chaotic.',
	flashcards: [
		{
			id: 'compounding-first-investing--investing-is-paid-uncertainty--fc-main',
			role: 'main_idea',
			front: 'What is paid uncertainty?',
			back: 'Accepting investment risk and price movement for the possibility of long-term growth.',
		},
		{
			id: 'compounding-first-investing--investing-is-paid-uncertainty--fc-watch',
			role: 'watch_out',
			front: 'What is a bad assumption?',
			back: 'Expecting growth without discomfort, drops, or uncertainty.',
		},
		{
			id: 'compounding-first-investing--investing-is-paid-uncertainty--fc-use',
			role: 'use_it',
			front: 'How do you sort money?',
			back: 'Match stable money to near-term needs and risk-taking money to longer timelines.',
		},
	],
	applyAction: {
		type: 'noToolAction',
		label: 'Sort money by job',
		prompt: 'Write one sentence naming which money in your life needs safety and which money could be long-term growth money.',
	},
	revision: 1,
};

const LESSON_5: Lesson = {
	id: 'compounding-first-investing--first-system-beats-perfect-timing',
	title: 'Your first system beats perfect timing',
	description:
		'You learn why a simple repeatable investing system matters more than trying to enter at the perfect moment.',
	pillar: 'compounding-investing',
	schoolsOfThought: [
		'boglehead-indexing',
		'behavioral-finance',
		'first-100k',
	],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['market-timing', 'investing-system', 'beginner-investor'],
	prerequisites: [
		'compounding-first-investing--investing-is-paid-uncertainty',
	],
	relatedLessons: [
		'compounding-first-investing--money-can-start-earning-too',
	],
	concepts: ['simple-system', 'timing-trap', 'consistency'],
	blocks: [
		{
			type: 'paragraph',
			text: 'The beginner question is usually, “Is now a good time?” The better first question is, “Do I have a system I can actually follow?” Timing feels smart because it gives you the illusion of control. A system is less exciting, but it is usually more useful.',
		},
		{
			type: 'paragraph',
			text: 'Perfect timing asks you to know what the market will do next. A first system asks simpler questions: How much can I repeat? What is this money for? What risk can I handle? What would make me panic? What do I do when life gets messy?',
		},
		{
			type: 'callout',
			icon: '🎯',
			text: 'The goal is not to predict every move. The goal is to build a process that survives normal life.',
		},
		{
			type: 'example',
			label: 'Two beginners',
			text: 'One person waits six months for the perfect moment and keeps second-guessing. Another sets a modest monthly amount, learns the basics, accepts that markets move, and reviews the plan calmly. The second person may not be smarter. They just gave their behavior a track to run on.',
		},
		{
			type: 'bullet_list',
			items: [
				'Pick a repeatable amount',
				'Understand the purpose',
				'Expect movement',
				'Avoid panic checking',
				'Review without constantly restarting',
			],
		},
		{
			type: 'paragraph',
			text: 'This does not mean blindly throwing money anywhere. It means your first win is not genius. Your first win is structure. A simple approach can protect you from the drama of constantly reacting to headlines, hot tips, and fear.',
		},
		{
			type: 'paragraph',
			text: 'The hidden cost of waiting for perfect timing is that your money life stays untrained. You do not learn your emotions. You do not build repetition. You do not create a base. You stay stuck in research mode and call it being careful.',
		},
		{
			type: 'key_takeaway',
			text: 'Your first investing edge is not prediction; it is a simple system you can keep following.',
		},
	],
	audioScript:
		'A lot of beginners think investing starts with the perfect moment. They ask, “Is now the right time?” That question feels responsible, but it can also become a trap. Because if you need perfect certainty before starting, you may never start. A better beginner question is, “What system could I follow even when the market gets noisy?” That is the real game. A system does not need to be fancy. It needs to be repeatable. How much can you set aside without wrecking your life? What is the money for? How long can it stay invested? What kind of drop would make you panic? What rule keeps you from constantly restarting? This is not advice about what to buy. It is a behavior lesson. People usually hurt themselves by trying to feel clever at the exact wrong moment. They chase when excited and quit when scared. A simple system gives you something sturdier than mood. It also gives you experience. You learn how markets feel. You learn how your brain reacts. You learn how to keep money pointed at the future. The first system beats perfect timing because perfect timing asks you to predict the world. A system asks you to manage yourself.',
	flashcards: [
		{
			id: 'compounding-first-investing--first-system-beats-perfect-timing--fc-main',
			role: 'main_idea',
			front: 'What beats perfect timing?',
			back: 'A simple repeatable system that can survive normal life and market noise.',
		},
		{
			id: 'compounding-first-investing--first-system-beats-perfect-timing--fc-watch',
			role: 'watch_out',
			front: 'What is research mode?',
			back: 'Using endless learning or waiting as a way to avoid starting a real system.',
		},
		{
			id: 'compounding-first-investing--first-system-beats-perfect-timing--fc-use',
			role: 'use_it',
			front: 'What should a system answer?',
			back: 'Amount, purpose, timeline, risk, and what you do when markets move.',
		},
	],
	applyAction: {
		type: 'openImpactScenario',
		label: 'Compare starting 18 vs 28',
		scenarioId: 'start-at-18-vs-28',
	},
	revision: 1,
};

const ASSESSMENT: ModuleAssessment = {
	moduleId: 'compounding-first-investing',
	checkpointQuiz: [
		{
			type: 'multiple_choice',
			id: 'compounding-first-investing--cq--q1',
			question: 'What is the main belief shift behind compounding?',
			options: [
				'Money only matters when you earn more of it',
				'Some money can be kept and given a chance to work over time',
				'Compounding removes investment risk',
				'Small amounts are useless until your income is high',
			],
			correctIndex: 1,
			explanation:
				'Compounding teaches that money can potentially grow on earlier growth when it is kept, invested, and given time. It does not remove risk.',
		},
		{
			type: 'true_false',
			id: 'compounding-first-investing--cq--q2',
			question:
				'Small investing amounts can still matter because they build repetition and a base.',
			correct: true,
			explanation:
				'The first amounts may not look impressive, but they build the system and habit that future progress depends on.',
		},
		{
			type: 'scenario',
			id: 'compounding-first-investing--cq--q3',
			scenario:
				'Maya earns $19 an hour and can only protect $75 a month right now.',
			question: 'What is the strongest Nisk-style takeaway?',
			options: [
				'She should wait until she can invest $500 a month',
				'The amount is too small to teach anything',
				'A repeatable $75 habit can still build discipline and future options',
				'She should ignore risk because the amount is small',
			],
			correctIndex: 2,
			explanation:
				'The point is not that $75 solves everything. The point is that repeated small amounts build the habit and base.',
		},
		{
			type: 'multiple_choice',
			id: 'compounding-first-investing--cq--q4',
			question: 'Why does time matter in compounding?',
			options: [
				'It guarantees positive returns',
				'It gives contributions and possible growth more years to build',
				'It makes investing risk-free',
				'It removes the need for income',
			],
			correctIndex: 1,
			explanation:
				'Time gives habits and possible growth more room to work, but it does not guarantee outcomes or remove risk.',
		},
		{
			type: 'true_false',
			id: 'compounding-first-investing--cq--q5',
			question: 'Starting later means compounding no longer matters.',
			correct: false,
			explanation:
				'Starting earlier helps, but starting later still improves direction. The plan may simply need stronger expectations and habits.',
		},
		{
			type: 'scenario',
			id: 'compounding-first-investing--cq--q6',
			scenario:
				'Chris says, “I missed my chance because I did not start at 22.”',
			question: 'What is the best response?',
			options: [
				'Correct, there is no point starting now',
				'Regret is useful only if it leads to action instead of paralysis',
				'He should take bigger risks to catch up instantly',
				'He should avoid learning about investing',
			],
			correctIndex: 1,
			explanation:
				'The lesson is not shame. It is that waiting has a cost, so refusing to wait longer matters.',
		},
		{
			type: 'multiple_choice',
			id: 'compounding-first-investing--cq--q7',
			question: 'What is the common trap with small money?',
			options: [
				'Thinking it can build habits',
				'Thinking it is fake money because it is not impressive yet',
				'Using it to learn carefully',
				'Repeating it monthly',
			],
			correctIndex: 1,
			explanation:
				'Dismissing small amounts can kill the habit before compounding and repetition get a chance to matter.',
		},
	],
	finalTest: [
		{
			type: 'multiple_choice',
			id: 'compounding-first-investing--final--q1',
			question: 'What does “money can start earning too” mean?',
			options: [
				'Money replaces work immediately',
				'Money can be placed where it has a chance to grow over time',
				'Money always grows if invested',
				'Money should never be spent',
			],
			correctIndex: 1,
			explanation:
				'The idea is that some money can be kept and exposed to possible long-term growth. It is not a promise and not anti-spending.',
		},
		{
			type: 'scenario',
			id: 'compounding-first-investing--final--q2',
			scenario:
				'Jordan has $1,000 for emergencies and is thinking about investing it all because investing sounds more powerful.',
			question: 'Which lesson applies best?',
			options: [
				'All money has the same job',
				'Emergency money and investing money should not be treated the same',
				'Investing removes the need for cash',
				'If money can grow, it cannot fall',
			],
			correctIndex: 1,
			explanation:
				'Money needed soon usually needs stability. Long-term growth money can usually tolerate more movement.',
		},
		{
			type: 'true_false',
			id: 'compounding-first-investing--final--q3',
			question:
				'Investing is best understood as accepting uncertainty for the possibility of long-term growth.',
			correct: true,
			explanation:
				'Investment risk and price movement are part of the tradeoff. Growth is possible, not promised.',
		},
		{
			type: 'multiple_choice',
			id: 'compounding-first-investing--final--q4',
			question:
				'What is the problem with waiting for the “perfect time” to invest?',
			options: [
				'It may keep your money life untrained and stuck in hesitation',
				'It always improves returns',
				'It makes risk disappear',
				'It proves you understand markets',
			],
			correctIndex: 0,
			explanation:
				'Waiting can become avoidance. A simple system often teaches more than endless timing attempts.',
		},
		{
			type: 'scenario',
			id: 'compounding-first-investing--final--q5',
			scenario:
				'A beginner invests $500 and sees the balance drop to $450.',
			question: 'What is the most accurate interpretation?',
			options: [
				'They automatically failed',
				'A drop can happen because markets move and risk is part of investing',
				'Investing is fake',
				'They should never learn again',
			],
			correctIndex: 1,
			explanation:
				'A lower balance does not automatically mean failure. It may reflect normal volatility, which should be understood before investing.',
		},
		{
			type: 'multiple_choice',
			id: 'compounding-first-investing--final--q6',
			question:
				'What is the strongest first investing “edge” for a beginner?',
			options: [
				'Predicting the next market move',
				'Following hot tips',
				'Building a simple repeatable system',
				'Checking the account every hour',
			],
			correctIndex: 2,
			explanation:
				'Beginners usually benefit from structure, consistency, and emotional training more than prediction.',
		},
		{
			type: 'true_false',
			id: 'compounding-first-investing--final--q7',
			question:
				'Compounding means every year will be positive and smooth.',
			correct: false,
			explanation:
				'Compounding describes growth building on growth over time, but actual investing can be uneven and negative in some periods.',
		},
		{
			type: 'scenario',
			id: 'compounding-first-investing--final--q8',
			scenario:
				'Lina says, “I only have $50 left after bills, so investing lessons are not for me.”',
			question: 'What should this module help her see?',
			options: [
				'$50 is automatically enough for every goal',
				'$50 is worthless',
				'Small repeated amounts can still teach the habit of keeping money and building future options',
				'She should feel ashamed for starting small',
			],
			correctIndex: 2,
			explanation:
				'The point is practical and non-shaming: modest amounts can still build repetition, identity, and a base.',
		},
		{
			type: 'multiple_choice',
			id: 'compounding-first-investing--final--q9',
			question: 'What is the hidden cost of dismissing small amounts?',
			options: [
				'You avoid all risk',
				'You may never build the repetition that larger progress depends on',
				'You become too consistent',
				'You save too much time',
			],
			correctIndex: 1,
			explanation:
				'Small amounts are often the training ground for larger future behavior.',
		},
		{
			type: 'scenario',
			id: 'compounding-first-investing--final--q10',
			scenario:
				'Devon reads investing headlines every week but never creates a simple plan.',
			question: 'What trap is Devon likely in?',
			options: [
				'Healthy long-term review',
				'Research mode that avoids action',
				'Emergency planning',
				'Cash flow tracking',
			],
			correctIndex: 1,
			explanation:
				'Endless learning can become avoidance if it never turns into a repeatable system.',
		},
		{
			type: 'true_false',
			id: 'compounding-first-investing--final--q11',
			question:
				'Nisk should frame investing as education and projection, not individual instructions on what to buy.',
			correct: true,
			explanation:
				'The content teaches principles, tradeoffs, and testing ideas. It does not prescribe specific financial decisions.',
		},
		{
			type: 'multiple_choice',
			id: 'compounding-first-investing--final--q12',
			question:
				'What best describes the relationship between time and risk in this module?',
			options: [
				'More time removes all investment risk',
				'Longer timelines can help absorb volatility but do not guarantee outcomes',
				'Risk only exists for late starters',
				'Time and risk are unrelated',
			],
			correctIndex: 1,
			explanation:
				'Time gives volatility more room to average out, but it never makes growth certain or risk-free.',
		},
	],
};

export const COMPOUNDING_FIRST_INVESTING_MODULE: LearningModule = {
	id: 'compounding-first-investing',
	title: 'Compounding & First Investing',
	description:
		'Investing is not magic — it is letting some dollars work beside you. Learn how compounding, time, and a simple repeatable system matter more than perfect timing, and why investing is paid uncertainty, not free money.',
	emoji: '🌱',
	accentColor: '#c8ff00',
	tier: 'growth',
	pillar: 'compounding-investing',
	order: 6,
	lessons: [LESSON_1, LESSON_2, LESSON_3, LESSON_4, LESSON_5],
	assessment: ASSESSMENT,
};
